import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '../../lib/utils';

export type DraggableState =
    | 'default'
    | 'selected'
    | 'used'
    | 'dragging'
    | 'correct'
    | 'incorrect';

export interface DraggableItemProps {
    id: string;
    children: React.ReactNode;
    state?: DraggableState;
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    /** Hide the source while dragging; pair with DragOverlay from @dnd-kit/core */
    useDragOverlay?: boolean;
    /**
     * EAA-1680: which playful-skin pressable hook to stamp — 'option' for
     * row/card-sized draggables (e.g. match answer options), 'chip' for
     * small tokens (e.g. FITB drag-bank words). See
     * docs/PLAN-Playful-Interactive-Options.md Phase 4.
     */
    surface?: 'option' | 'chip';
}

export const DraggableItem = React.forwardRef<HTMLDivElement, DraggableItemProps>(
    (
        {
            id,
            children,
            state = 'default',
            disabled = false,
            onClick,
            className,
            style,
            useDragOverlay = false,
            surface = 'option',
        },
        ref,
    ) => {
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id,
            disabled,
        });

        const effectiveState = isDragging ? 'dragging' : state;
        const isInteractive = !disabled && effectiveState !== 'used';
        const hookClass = surface === 'chip' ? 'content-chip' : 'content-option';

        const mergedRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                setNodeRef(node);
                if (typeof ref === 'function') ref(node);
                else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            },
            [setNodeRef, ref],
        );

        return (
            <div
                ref={mergedRef}
                {...(isInteractive ? { ...attributes, ...listeners } : {})}
                className={cn(
                    'rounded-sm border bg-muted/30 p-2 relative',
                    !isDragging &&
                        'transition-[transform,box-shadow,opacity,border-color,background-color] duration-200 ease-out',
                    isInteractive && 'cursor-grab active:cursor-grabbing touch-none',
                    effectiveState === 'selected' && 'ring-2 ring-primary',
                    effectiveState === 'used' && 'opacity-40 scale-[0.97]',
                    effectiveState === 'dragging' && 'z-50 shadow-xl scale-105 border-primary/60 bg-background cursor-grabbing',
                    effectiveState === 'correct' && 'border-success bg-success/10',
                    effectiveState === 'incorrect' && 'border-destructive bg-destructive/10 animate-shake',
                    disabled && !['correct', 'incorrect'].includes(effectiveState) && 'cursor-default',
                    // EAA-1680: playful-skin pressable hooks. Static covers
                    // 'used' (locked chip) same as disabled; dragging maps to
                    // its own modifier instead of relying on :active since
                    // dnd-kit's pointer sensor already owns the press.
                    hookClass,
                    (disabled || effectiveState === 'used') && `${hookClass}--static`,
                    effectiveState === 'selected' && `${hookClass}--selected`,
                    effectiveState === 'dragging' && `${hookClass}--dragging`,
                    effectiveState === 'correct' && 'content-feedback--correct',
                    effectiveState === 'incorrect' && 'content-feedback--incorrect',
                    className
                )}
                style={{
                    ...style,
                    transform: transform
                        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
                        : style?.transform,
                    opacity: useDragOverlay && isDragging ? 0 : style?.opacity,
                    transition: isDragging ? 'none' : style?.transition,
                } as React.CSSProperties}
                onClick={() => isInteractive && onClick?.()}
            >
                {children}
            </div>
        );
    },
);

DraggableItem.displayName = 'DraggableItem';
